import {
  execute,
  GetBlogCategoriesDocument,
  GetBlogPostsDocument,
  PostSortTypeEnum
} from '@/fo/api/magento';
import { FeatureBannerDataBase } from '@/fo/feature/banner/data/base';
import {
  mapArticleBannersFromData,
  UiArticleSectionImportant
} from '@/fo/ui/article/section/important';
import { UiArticleSectionProjects } from '@/fo/ui/article/section/projects';
import {
  UiKitSectionSeoText,
  mapSeoBannerFromData
} from '@/fo/ui/kit/section/seo-text';

import { getPosts } from '../api/getPosts';
import { PageBlogClient } from '../components/PageBlogClient/PageBlogClient';
import { BANNER_PAGE_SIZE, TOPICS_SORT_TYPE, bannersMap } from '../consts';
import {
  getTopicsSectionData,
  mapAnotherPost,
  mapArticleCategories,
  mapCategories,
  mapPostsToBanner,
  mapTopicArticles,
  loadBlogSearchParams
} from '../helpers';
import { BlogPageTopic, BlogPageType } from '../types';

interface FeatureArticlePageBlogProps {
  searchParams: Record<string, string | string[] | undefined>;
  pageType: BlogPageType;
  topicKey: BlogPageTopic;
  anotherType: BlogPageType;
  topicPageSize: number;
}

export async function FeatureArticlePageBlog({
  searchParams,
  pageType,
  topicKey,
  anotherType,
  topicPageSize
}: FeatureArticlePageBlogProps) {
  const { sort, categories, query, p } =
    await loadBlogSearchParams(searchParams);

  const [postsList, bannerList, categoriesList, topicList, anotherPostsList] =
    await Promise.all([
      getPosts({
        type: pageType,
        sort,
        categories,
        query,
        currentPage: p
      }),
      execute(GetBlogPostsDocument, {
        action: 'get_post_list',
        type: pageType,
        pageSize: BANNER_PAGE_SIZE,
        currentPage: 1,
        sortBy: PostSortTypeEnum.Latest
      }),
      execute(GetBlogCategoriesDocument, {
        action: 'get_category_list',
        type: pageType
      }),
      execute(GetBlogPostsDocument, {
        action: 'get_post_by_topic',
        type: pageType,
        pageSize: topicPageSize,
        currentPage: 1,
        sortBy: TOPICS_SORT_TYPE,
        topicKey: topicKey
      }),
      execute(GetBlogPostsDocument, {
        action: 'get_post_list',
        type: anotherType,
        pageSize: 1,
        currentPage: 1,
        sortBy: TOPICS_SORT_TYPE
      })
    ]);

  const mappedCategories =
    pageType === BlogPageType.Article
      ? mapArticleCategories(categoriesList.data?.mpBlogCategories)
      : undefined;

  const filters =
    pageType !== BlogPageType.Article
      ? mapCategories(categoriesList.data?.mpBlogCategories)
      : undefined;

  const categoriesWithActive =
    mappedCategories?.map((category) => ({
      ...category,
      active: categories.includes(String(category.id))
    })) ?? [];

  const mappedBanners = mapPostsToBanner(
    bannerList.data?.mpBlogPosts,
    pageType
  );
  const mappedTopicArticles = mapTopicArticles(
    topicList.data?.mpBlogPosts,
    pageType
  );
  const mappedAnotherPost = await mapAnotherPost(
    anotherPostsList.data?.mpBlogPosts,
    anotherType
  );

  const { title, description, allPostsLabel, readAnotherPostLabel } =
    await getTopicsSectionData(topicKey);

  return (
    <>
      <PageBlogClient
        pageType={pageType}
        topicKey={topicKey}
        topicTitle={title}
        topicDescription={description}
        bannerList={mappedBanners}
        categoriesList={categoriesWithActive}
        postsList={postsList.posts}
        totalPages={postsList.totalPages}
        topicArticles={mappedTopicArticles}
        anotherPost={mappedAnotherPost}
        allPostsLabel={allPostsLabel}
        readAnotherPostLabel={readAnotherPostLabel}
        filters={filters}
      />
      <UiArticleSectionProjects />
      <FeatureBannerDataBase slug="slider_navigation">
        {(banners) => (
          <UiArticleSectionImportant
            articles={mapArticleBannersFromData(banners)}
          />
        )}
      </FeatureBannerDataBase>

      <FeatureBannerDataBase slug={bannersMap[pageType]}>
        {(banners) => (
          <UiKitSectionSeoText {...mapSeoBannerFromData(banners[0])} />
        )}
      </FeatureBannerDataBase>
    </>
  );
}
